import { batchStatusLabel, t } from '@ctcells/i18n'
import { useControlDisabled } from '@ctcells/realtime'

import { Button } from '../atoms/Button.tsx'
import { Icon, type IconName } from '../atoms/Icon.tsx'
import { StatusBadge } from '../atoms/StatusBadge.tsx'
import { HALT_GLYPH } from '../screens/batches/haltLine.ts'
import { moduleLabelOf } from '../screens/format.ts'

import type { BatchCommand, BatchStripProps } from './batchStripTypes.ts'
import { batchHaltStore } from './batchHaltStore.ts'
import { useExplicitStripPick } from './stripPick.ts'
import { ControlLockChip } from './ControlLockChip.tsx'
import { startBlockersText } from './startBlockers.ts'
import { StripBatchPicker } from './StripBatchPicker.tsx'
import { StripBrief, StripStages } from './StripStages.tsx'
import { stripCellIsNow, stripFlowZones, stripStageCells, stripStepCarried } from './stripStages.ts'
import { WaitReasonBadge } from './WaitReasonBadge.tsx'

import './batchStrip.css'

/**
 * 상시 작업 줄 — 디자인팀 시안 2026-09-30 §2 「상단 실행 영역」(`docs/design/CTcells-console.html` `run()` · `brief()`).
 * **앱 셸이 소유하고 콘솔 · 태블릿 · 패드가 같은 것을 쓴다.** 화면이 만들지 않는다.
 *
 * ```
 * 1행 [실행 중] 작업 이름  담당 <이름>  시작 <YYYY-MM-DD HH:MM:SS>  레시피 <이름>   … [이벤트 로그][제어락][일시정지][중지][복구] │ E-STOP
 * 2행 ① 원심분리기 [완료] │ ② 어셈블러 [완료] │ ③ 염색기 [이동 중] │ 전체 1,127 / 4,730 스텝 · 경과 00:23:18                │ 전체 비상 정지
 *     ████████ 176/176 100%  ████████ 343/343 100%  ███░░░░░ 608/4,211 14%                                                    │ (1·2행에 걸침)
 * ```
 * 축약형(`variant="compact"`)은 2행이 현재 공정 한 줄로 접힌다(`StripBrief`). 어느 모습인지는 **경로**가 정한다
 * (`stripVariantFor` · 셸) — 내용이 정하면 높이가 흔들린다. ⚠ 지금 축약형을 쓰는 셸은 태블릿뿐이다 — 통합 콘솔은
 * 2026-10-03 부터 어느 메뉴에서든 `full` 이고, 메뉴마다 갈리는 것은 접힘 기본값이다(eric 「다른 메뉴에 가도 모니터링에서의
 * 상시창 내용이 보이게」).
 *
 * 🔴 **높이가 절대 변하지 않는다.** eric 원문: "상태가 바뀌어도 높이가 다 달라.. 어지러워".
 *    조건부로 행을 없애지 않는다 — 두 행 높이를 CSS `grid-template-rows` 로 못박고(모습마다 고정값)
 *    비어 있을 때는 자리만 남긴다. IDLE · MANUAL 도 이름만 선 공정 칸 셋이 그대로 나온다.
 *    ⚠ 종전 3행(이전 ‹ 현재 › 다음 · 이벤트 토글)은 걷었다(2026-10-01) — 지금 스텝은 도는 공정 칸의 배지가 말하고,
 *      이벤트 토글은 1행 「이벤트 로그」 단추다. 실패 문구 · 안전정지 문구는 1행 안(레시피 오른쪽)에 선다.
 *
 * 🔴 **접힘(`collapsed`)은 상태가 아니라 화면 배치다**(2026-10-01 회의 — 노트북에서 본문이 좁다 · 2026-10-03 eric — 통합
 *    콘솔은 모니터링만 펼침이 기본이고 그 밖의 메뉴는 접힘이 기본이다 · 셸 `useStripFold`). 두 행이 1행 한 줄로
 *    줄고(`[펼치기][배지] 이름 [이상 표지] ① 공정 [배지] n / 3 공정 ▇░ 전체 n / m  [제어락] [명령] │ E-STOP`) 높이는 그 모습의
 *    고정값이다 — 작업 상태가 바뀌어도 접힌 높이는 안 흔들린다. 메타 · 레시피 · 이벤트 로그만 빠진다(명령 버튼은 2026-10-12 부터 남는다).
 *    ⚠ 통합 콘솔은 이벤트 로그가 2행 요약 카드 오른쪽 아래에 선다(`eventsInTally` · eric 2026-10-06) — 2행이 접히면 같이 빠진다.
 *    🔴 E-STOP 은 접어도 같은 버튼이다(높이만 1행).
 *
 * 🔴 **버튼 활성은 서버가 준 `allowedCommands` 그대로다.** 여기서 상태를 보고 계산하지 않는다.
 *    어떤 버튼이 주 버튼 자리에 오는지(표기)만 상태가 정하고, 눌리는지(활성)는 서버가 정한다.
 *
 * 🔴 **E-STOP 에 확인 대화상자를 붙이지 마라**(정본 07 §2.7). 역할 · 제어락 · 상태 · stale 무관
 *    항상 활성이다. 눌리면 즉시 나간다.
 */
/** 비상정지 중에 막는 명령 — 장비를 움직이거나 작업을 이어 가는 것(중지 · 복구는 뺀다 · `reasonFor`) */
const ESTOP_BLOCKED: ReadonlySet<BatchCommand> = new Set<BatchCommand>([
  'start',
  'pause',
  'resume',
  'load_confirm',
  'unload_confirm',
])

export function BatchStrip(props: BatchStripProps) {
  const streamStale = useControlDisabled()
  const {
    batch,
    variant = 'full',
    manual = false,
    lock,
    allowedCommands,
    permissions,
    safety,
    controlDisabled = streamStale,
    commandInFlight = false,
    startInFlight = false,
    startBlockers = [],
    commandError,
    eventCount,
    eventsOpen = false,
    events,
    onCommand,
    onPreflightFixes,
    onEmergencyStop,
    onClearSafety,
    onShowCause,
    onLockAction,
    onSelectBatch,
    onClearChoice,
    onToggleEvents,
    onOpenBatch,
    onOpenRecipe,
    onOpenMonitoring,
    onDismissError,
    disposition,
    commandErrorAction,
    eventsOnlyStarted = false,
    eventsInTally = false,
    flowZones = false,
    preflight,
    batchChoice,
    estopCaption = true,
    estimate,
    nameFirst = false,
    collapsed = false,
    onToggleCollapsed,
  } = props

  /*
   * 시작 요청이 나가는 동안 고르기를 숨긴다(통합 콘솔). ⚠ 종전의 `batch?.started` 항은 걷었다 — 이 값을 읽는 자리는 작업이 «없는»
   * 갈래뿐이라(작업이 서면 [작업 선택] 자체가 없다 · eric 2026-10-24) 늘 거짓이었다. 상태명으로 시작을 추론하지 않는다.
   */
  const hideStartControls = flowZones && startInFlight
  /* 고른 작업 풀기 × — 컨테이너가 «고른 시작 전 작업»일 때만 싣는다. 시작 요청 중 · 시작한 작업(서버 사실)이면 안 선다 */
  const clearChoiceShown = onClearChoice !== undefined && batch?.started !== true && !startInFlight

  const hasControl = permissions?.includes('control') ?? false
  const lockHeld = lock.kind === 'mine'

  /**
   * 비활성 사유 — 하나만 고른다. 우선순위가 곧 "가장 먼저 풀어야 할 조건"이다(§5-8).
   * 스트림이 죽었으면 나머지는 볼 필요가 없어서 맨 위다.
   *
   * 🔴 여기까지가 **`allowed_commands` 축이 «없는» 동작**(안전 해제)도 함께 쓰는 공통 축이다.
   */
  const baseReason = ((): string | undefined => {
    if (controlDisabled) return t('reason.streamStale')
    // 🔴 「보내는 중」이 권한·락보다 «먼저»다. 셋 다 통과한 사람이 두 번 누르는 상황이라
    //    다른 사유를 먼저 보여 주면 왜 안 눌리는지 설명이 안 된다
    if (commandInFlight) return t('reason.actionInFlight')
    if (!hasControl) return t('reason.noPermission', { perm: 'control' })
    if (!lockHeld) return t('reason.lockRequired')
    return undefined
  })()

  /**
   * 시작이 막힌 사유 전문(`startBlockers()` 의 대조 결과). 🔴 서버가 `start` 를 실었으면
   * 컨테이너가 빈 배열을 준다 — 이 값이 버튼을 켜거나 끄지 않는다. 문구만 바꾼다.
   */
  const startText = startBlockersText(startBlockers)

  const reasonFor = (command: BatchCommand): string | undefined => {
    /*
     * 🔴 **비상정지 중에는 장비를 움직이는 명령을 막는다**(eric 2026-10-14 「E-STOP 을 누르면.. 재개 버튼도 활성화되어 있어..
     *    E-STOP 상태에서 활성화되면 안 되는 버튼은 비활성화로 해줘」). 서버 `allowed_commands` 는 안전 상태를 보지 않아
     *    `paused` 면 `resume` 을 싣는다 — 눌러도 발행 게이트가 거절한다(`batches.refuse_if_blocked`). 그 거절을 미리 비활성 + 사유로 보인다.
     *    중지 · 복구는 막지 않는다 — 폐기는 비상정지 중에도 서버가 받는다(`recover_discard` · 막는 조건 없음). 복구 대화 안의 「재개」는
     *    대화가 따로 막는다(`StripCommandDialog` `estopped`). 끊김(stale)은 «지금»을 모르니 그 사유가 먼저다.
     */
    if (!controlDisabled && safety.state === 'emergency_stop' && ESTOP_BLOCKED.has(command))
      return t('reason.estopActive')
    /*
     * 🔴 **확인 재개 멈춤에는 [재개]를 막는다**(`resume_route=confirm` · 백엔드 0.28.15). 서버 `allowed_commands` 는 상태표만 봐서
     *    `resume` 을 싣지만 재개 창구가 `cut_confirm_required` 로 거절한다 — 위 비상정지와 같은 결로, 그 거절을 미리 비활성 + 사유로
     *    보인다. 잇는 길은 [복구] «멈춘 동작부터 다시»다(주 버튼도 [복구]로 선다). 서버 칸을 읽는 것이지 상태 추론이 아니다.
     */
    if (!controlDisabled && command === 'resume' && batch?.resumeRoute === 'confirm')
      return t('reason.resumeNeedsConfirm')
    /*
     * 🔴 **멈춘 작업의 [중지]는 끝내는 길을 말한다**(일시정지 · 비상정지 뒤 [중지]가 「지금 상태에서는 할 수 없음」뿐이었다).
     *    서버는 `paused` · `error` 에서 중지를 받지 않는다(core `FROM_STOP` · `stop_batch` 「그 중단은 폐기 결정이다」) —
     *    끝내는 것은 [복구 › 폐기]다. ⚠ **문구만 고른다** — 활성은 여전히 `allowedCommands` 다(서버가 `stop` 을 실으면 이 갈래를 안 탄다).
     *    권한이 없으면 권한 사유가 먼저다. 제어락이 없어도 이 문구다 — 락을 쥐어도 중지는 안 받으니 이쪽이 먼저 풀 조건이다.
     */
    if (
      !controlDisabled &&
      !commandInFlight &&
      hasControl &&
      command === 'stop' &&
      !allowedCommands.includes('stop') &&
      (batch?.status === 'paused' || batch?.status === 'error')
    )
      return t('reason.stopWhileHalted')
    // 🔴 stale · 요청 중은 «지금» 사실이라 먼저 말한다. 역할·락은 사유 목록이 같이 담는다
    if (
      command === 'start' &&
      startText !== undefined &&
      !allowedCommands.includes('start') &&
      !controlDisabled &&
      !commandInFlight
    )
      return startText
    return (
      baseReason ?? (allowedCommands.includes(command) ? undefined : t('reason.commandNotAllowed'))
    )
  }

  /** 제어락 획득·반납도 상태 변경이다 — stale · 권한 없음이면 같이 막힌다 */
  const lockGateReason = controlDisabled
    ? t('reason.streamStale')
    : commandInFlight
      ? t('reason.actionInFlight')
      : hasControl
        ? undefined
        : t('reason.noPermission', { perm: 'control' })

  const actionProps = (command: BatchCommand) => {
    const reason = reasonFor(command)
    return reason === undefined
      ? ({ disabled: false, onClick: () => onCommand?.(command) } as const)
      : ({ disabled: true, disabledReason: reason } as const)
  }

  /**
   * 주 버튼 자리는 하나이고 **표기만** 상태가 정한다(§13 표).
   * 적재·수거 확약은 서버가 그 명령을 허용했을 때만 그 얼굴로 바뀐다 —
   * 프론트가 모듈 상태를 보고 추론하지 않는다.
   */
  const primary = ((): { command: BatchCommand; label: string; icon: IconName } => {
    if (allowedCommands.includes('load_confirm'))
      return { command: 'load_confirm', label: t('action.loadDone'), icon: 'check' }
    if (allowedCommands.includes('unload_confirm'))
      return { command: 'unload_confirm', label: t('action.unloadDone'), icon: 'check' }
    if (batch?.status === 'running')
      return { command: 'pause', label: t('action.pause'), icon: 'pause' }
    // 서버의 resume_route가 review_reason보다 먼저다(C-157: 확인된 격리는 resume에서도 해제).
    /*
      🔴 **복구 대기 까닭이 서 있으면 «복구»가 주 버튼이다** — `paused` 라도 그렇다.
         계약이 그렇게 시켰다: 「boolean 이 아닌 까닭은 «기능»이다 — 화면이 버튼을 갈라야
         한다」(`Batch.review_reason`). **서버가 준 칸을 읽는 것이지 상태 추론이 아니다.**
      🔴 **실물 사고 때문에 넣었다**(2026-09-21 · BR-50). `handoff_unconfirmed` 에서 주 버튼이
         평범한 `resume` 이었고, 그 명령은 보상이 세운 **격리 락을 놓지 않는다.** 되돌아오고,
         이어서 [중지]를 누르면 `aborted` 가 되어 그 락을 풀 배치 경로가 **사라진다.**
         격리를 놓는 것은 `recover` 하나뿐이다.
      ⚠ **활성은 그래도 `allowedCommands`** 다 — `recover` 가 안 실려 오면 비활성으로 뜬다.
         서버가 허용하지 않는 것을 이 갈래가 켜 주지 않는다.
      🔴 **`recovery_decision` 은 이 갈래에 넣지 않는다**(eric 2026-10-14 「일시 정지 버튼을 누르면.. 재개가 표시되어야 하는 거
         아니야?」). 서버는 `paused`·`error` 면 **늘** 그 까닭을 준다(`services/batches.review_reason` — 사람이 누른 일시정지 포함).
         여기 넣으면 일시정지마다 주 버튼이 [복구]가 되어 [재개]가 사라졌다. 계약도 그 까닭의 버튼을 「[재개][폐기]」로 적었고,
         `resume` 이 격리를 못 놓는 문제(BR-50)는 `handoff_unconfirmed` 몫이다. [복구]는 옆 자리에 그대로 선다.
    */
    if (batch?.resumeRoute === 'resume')
      return { command: 'resume', label: t('action.resume'), icon: 'play' }
    if (
      batch?.resumeRoute === 'confirm' ||
      (batch?.resumeRoute == null &&
        (batch?.reviewReason === 'handoff_unconfirmed' ||
          batch?.reviewReason === 'completion_unobserved'))
    )
      return { command: 'recover', label: t('action.recover'), icon: 'recover' }
    /*
      🔴 **멈춤 한 줄이 「오류가 풀렸습니다」면 [재개]가 주 버튼이다**(정본 07 §4.12 「+ [재개] 강조」). 서버 칸
         (`halt_resume=human` ∧ `resume_blockers=[]`)을 읽는 것이지 상태 추론이 아니다. 복구 대기 까닭이 서 있으면
         위 갈래가 먼저다(BR-50 — 격리를 놓는 것은 `recover` 뿐).
      ⚠ «얼굴»만 고른다 — 활성은 여전히 `allowedCommands` 다. 서버가 `resume` 을 안 실으면 비활성 + 사유로 선다.
    */
    if (batch?.halt?.kind === 'cleared')
      return { command: 'resume', label: t('action.resume'), icon: 'play' }
    if (batch?.status === 'paused')
      return { command: 'resume', label: t('action.resume'), icon: 'play' }
    if (batch?.status === 'error')
      return { command: 'recover', label: t('action.recover'), icon: 'recover' }
    return { command: 'start', label: t('action.start'), icon: 'play' }
  })()

  const haltPopupEnabled = useExplicitStripPick()
  const hasHaltPopup =
    haltPopupEnabled && batch && (batch.status === 'paused' || batch.status === 'error')

  const estopped = safety.state === 'emergency_stop'
  /*
   * 🔴 **멈춘 작업의 안전정지 · E-STOP 은 맨 왼쪽 상태 배지가 말한다**(eric 2026-10-29 「여기에 E-STOP 빨갛게」 → 「이거를 일시정지에다가 넣어」).
   *    전체 E-STOP(`safety.estop` · 멈춤 팝업과 같은 판정 `estopMode.ts`)이면 붉은 「▲ E-STOP」, 그 밖의 안전정지면 붉은 「▲ 안전정지」 —
   *    같은 배지 크기 · 위험 톤. 따로 서던 안전정지 칩은 그때 걷힌다(머리 줄 칩 하나가 준다). 작업이 없거나 멈추지 않았으면 칩이 종전대로 말한다
   */
  const haltSafetyBadge = estopped && batch != null && batch.status === 'paused'
  const safetyUnknown = safety.state === 'unknown'

  /* 2행 공정 칸 셋의 재료 — 1행(지금 스텝 문구를 어디에 적을지)도 같은 것을 본다 */
  const cells = stripStageCells(batch)
  /* 준비 · 완료 영역(통합 콘솔) — 2행이 장비 칸 사이에 끼우고, 접힌 줄 · 축약형은 지금인 준비 영역을 말한다 */
  const zones = flowZones ? stripFlowZones(batch, cells) : undefined
  /* 지금 스텝 문구를 칸이 지고 있나 — 지금인 준비 영역도 그 자리다(배지가 「준비 중」을 말한다) */
  const stepCarried = stripStepCarried(cells) || (zones?.some(stripCellIsNow) ?? false)

  /* 이름이 길면 말줄임된다 — 툴팁이 「이름 — UUID」 전체를 말한다(이름이 없으면 UUID 하나) */
  const nameTitle =
    batch?.name === undefined || batch.name === batch.id ? batch?.id : `${batch.name} — ${batch.id}`

  /*
   * 1행 메타 — **라벨 달린 항목**으로 가른다(디자인팀 시안 2026-09-30 §2 「무엇이 담당이고 무엇이 시각인지 바로 읽힘」).
   * 종전 서버 이름 「fe-… · 로보스 · 09-30 14:45」처럼 가운뎃점으로 잇지 않는다.
   * - 「담당 <이름>」 — `ownerText` 가 있을 때만(지금 세션의 사람이 만든 작업 · `stripOwnerText`). 남의 ID 는 안 깐다
   * - 「시작 <YYYY-MM-DD HH:MM:SS>」 — 연월일시분초 전부(`fullDateTimeText`). 시안의 `09-29 11:10` 짧은 꼴은 안 쓴다.
   *   `started_at` 이 없으면 「시작 전」
   * - 「레시피 <이름>」 — 시안 `.recipe`. 「레시피 보기」 링크가 종전부터 있던 자리다(`docs/design/DESIGN-CHANGES.md` §5).
   *   이동이 없으면 글자다
   * ⚠ 대기(`created`) 작업은 이름과 함께 이 칸도 안 선다(배지 「대기」만).
   *
   * 🔴 **차례: 레시피 → 작업자 → 시작**(eric 2026-10-12 「테스트 작업 다음에 레시피를 맨 처음으로, 그 다음에 담당, 시작을 맨 마지막에.. 담당을
   *    작업자라고」). 종전 차례(담당 → 시작 → 레시피)는 뒤집혔다.
   * 🔴 **좁으면 «뒤에서부터 통째로» 빠진다 — 시작 → 작업자 → 레시피**(2026-10-01 회의 「노트북 화면」 · 1280 에서 세 항목이
   *    서로 겹쳐 그려졌다). 세 항목이 한 줄 높이로 잘린 감싸기 칸(`flex-wrap`)에 서서, 자리가 모자란 항목은 둘째 줄로
   *    넘어가 안 보인다 — 미디어 쿼리 · 폭 숫자 없이 수축 순서로 푼다(`batchStrip.css` `.strip__meta`). 마지막 「담당」은
   *    말줄임으로 준다. 빠진 값은 DOM 에 남아 화면 읽기 프로그램이 그대로 읽고, 마우스는 이 칸의 툴팁(`title`)이 말한다.
   */
  const metaTitle =
    batch === undefined || batch === null
      ? undefined
      : [
          `${t('strip.metaRecipe')} ${batch.recipeName}`,
          ...(batch.ownerText === undefined ? [] : [`${t('strip.metaOwner')} ${batch.ownerText}`]),
          `${t('strip.metaStarted')} ${batch.startedAtText ?? t('strip.notStarted')}`,
        ].join('\n')
  const meta =
    batch === undefined || batch === null ? null : (
      <span className="strip__meta" title={metaTitle}>
        <span className="strip__metaitem strip__recipe">
          <span className="strip__metalabel">{t('strip.metaRecipe')}</span>
          {onOpenRecipe === undefined ? (
            <span className="strip__title">{batch.recipeName}</span>
          ) : (
            <button type="button" className="strip__title lnk" onClick={onOpenRecipe}>
              {batch.recipeName}
              <span className="a-sr">{t('strip.openRecipe')}</span>
            </button>
          )}
        </span>
        {batch.ownerText === undefined ? null : (
          <span className="strip__metaitem">
            <span className="strip__metalabel">{t('strip.metaOwner')}</span>
            <span className="strip__metavalue">{batch.ownerText}</span>
          </span>
        )}
        <span className="strip__metaitem">
          <span className="strip__metalabel">{t('strip.metaStarted')}</span>
          <span className="strip__metavalue num">
            {batch.startedAtText ?? t('strip.notStarted')}
          </span>
        </span>
      </span>
    )

  /*
   * 1행의 **알림 자리** — 레시피 오른쪽, 조작 왼쪽. 종전 3행이 맡던 두 말이 여기로 왔다(2026-10-01 · 3행 폐지):
   *   ① 안전정지 — 「안전정지 · CRITICAL N건」 + 원인 한 줄 + [안전 해제] [원인 보기]. 상단 배너가 아니다(§3-5)
   *   ② 명령 실패 문구 — `role="alert"` + (행동 하나) + 닫기 ×. 🔴 **스스로 사라지지 않는다**(DP-02)
   * 둘 다 있으면 안전정지가 이긴다. 🔴 **행을 새로 만들지 않는다** — 1행 «안»이고 길면 이 조각이 먼저 줄어든다
   * (`flex: 1000 1 0` · 말줄임). 오른쪽 제어 · E-STOP 은 안 움직인다.
   */
  const safetyCause = safety.alarm
    ? `${safety.alarm.code} · ${safety.alarm.module} · ${safety.alarm.message}`
    : undefined
  const notice = estopped ? (
    <span className="strip__notice strip__notice--safe">
      {haltSafetyBadge ? null : (
        <span
          className="strip__mod strip__mod--danger"
          {...(collapsed && safetyCause !== undefined ? { title: safetyCause } : {})}
        >
          {/*
           * 작업이 없거나 멈추지 않은 안전정지 — 위험(붉은) 칩 + ▲ 가 말한다(eric 2026-10-29 「E-STOP 빨갛게」). 색만으로 말하지 않게
           * 기호 · 글자가 같이 말한다. 접힌 줄에서는 원인 전문을 칩의 툴팁이 말한다. 칩은 줄지 않는다(`.strip--collapsed .strip__notice--safe`).
           * 멈춘 작업이면 이 칩 대신 맨 왼쪽 상태 배지가 말한다(`haltSafetyBadge`).
           */}
          <i aria-hidden="true" className="strip__modglyph">
            ▲
          </i>
          {safety.criticalCount === undefined
            ? t('strip.safetyStopped')
            : t('strip.safetyCritical', { count: safety.criticalCount })}
        </span>
      )}
      {/*
        🔴 정지 명령 도달 — 다 도달했거나(`reached === total` · 미도달 없음) 아직 값을 못 받았으면 칩을 그리지 않는다(eric 2026-10-29 「정지
           발송 · 도달 4/4 — 이게 무슨 말이야? 필요해?」 → 팀장 결정). 미도달 장비가 있을 때만 붉은 칩 + ▲ 「정지 미도달 n대 · 장비 이름」 —
           그 장비가 정지 명령을 못 받았을 수 있다는 위험 신호다(서버 `GET /safety` `stop_reach` 그대로). 장비 이름은 사람이 읽는 이름
           (`moduleLabelOf` · 모르는 ID 는 원값)이고 원래 ID 는 툴팁에 남긴다(2026-10-23 팀장 결정)
      */}
      {safety.stopReach != null && safety.stopReach.unreached.length > 0 ? (
        <span
          className="strip__mod strip__mod--danger strip__mod--unreached"
          title={safety.stopReach.unreached.join(', ')}
        >
          <i aria-hidden="true" className="strip__modglyph">
            ▲
          </i>
          {t('strip.stopUnreachedChip', {
            count: safety.stopReach.unreached.length,
            modules: safety.stopReach.unreached.map(moduleLabelOf).join(', '),
          })}
        </span>
      ) : null}
      {collapsed ? null : (
        <span className="strip__cur" title={safetyCause}>
          {safetyCause ?? ' '}
        </span>
      )}
      {/* /safety/clear는 비상정지만 풀고 로봇 정지·이송 격리는 남긴다 — 멈춘 작업은 팝업의 «현장 확인 후 정지 풀기»로 함께 푼다(eric 2026-10-28). */}
      {collapsed || hasHaltPopup ? null : (
        <span className="strip__sacts">
          {/*
           * 🔴 **안전 해제도 제어락이 필요하다** — 실서버가 `POST /safety/clear` 에
           *    `X-Control-Token` 을 걸고 `require_control_lock` 로 집행한다
           *    (`app/views/safety.py` 실측 2026-09-20). 종전에는 락 축이 빠져 있어서
           *    락 없는 사람에게 버튼이 열렸다가 서버에서 409 로 떨어졌다.
           * ⚠ `allowed_commands` 축은 없다 — 안전 해제는 그 집합에 실리는 명령이 아니다.
           */}
          <Button
            size="pill"
            {...(baseReason === undefined
              ? ({ disabled: false, onClick: onClearSafety } as const)
              : ({ disabled: true, disabledReason: baseReason } as const))}
          >
            {t('action.clearSafety')}
          </Button>
          <Button size="pill" onClick={onShowCause}>
            {t('action.showCause')}
          </Button>
        </span>
      )}
    </span>
  ) : commandError !== undefined ? (
    <span className="strip__notice strip__notice--error">
      <span className="strip__cur" role="alert" title={commandError}>
        {commandError}
      </span>
      {commandErrorAction === undefined ? null : (
        <Button
          size="pill"
          {...(baseReason === undefined
            ? ({ disabled: false, onClick: commandErrorAction.onClick } as const)
            : ({ disabled: true, disabledReason: baseReason } as const))}
        >
          {commandErrorAction.label}
        </Button>
      )}
      {/* 닫기 — 1행 «안»의 작은 글리프다. 행을 늘리지 않는다 */}
      <button
        type="button"
        className="strip__dismiss"
        onClick={onDismissError}
        aria-label={t('strip.dismissError')}
        title={t('strip.dismissError')}
      >
        <span aria-hidden="true">×</span>
      </button>
    </span>
  ) : !flowZones && (batch === undefined || batch === null) && disposition !== undefined ? (
    /*
     * 🔴 **시료 처분 대기 — 빈 띠에만**(`stripDisposition` · DP-02). [중지]한 작업은 띠에서 바로 빠지지만 시료 처분은 사람이 닫을 때까지
     *    남는다 — 그 사실을 여기 한 줄로 남기고 처분 창구(작업 상세)로 보낸다. 서버가 풀면 컨테이너가 안 싣는다.
     * 🔴 × 는 «이 화면에서 숨김»이다(서버 상태 그대로) — 닫기 단추의 접근 이름 · 툴팁이 그렇게 말한다.
     */
    <span className="strip__notice strip__notice--review">
      <span
        className="strip__cur"
        role="status"
        title={t('strip.disposition.title', { id: disposition.shortId })}
      >
        <i aria-hidden="true" className="strip__modglyph">
          ▲
        </i>
        {t('strip.disposition.text', { id: disposition.shortId })}
        {disposition.more > 0 ? ` ${t('strip.disposition.more', { count: disposition.more })}` : ''}
      </span>
      {disposition.onOpen === undefined ? null : (
        <button type="button" className="lnk" onClick={disposition.onOpen}>
          {t('strip.disposition.open')}
        </button>
      )}
      <button
        type="button"
        className="strip__dismiss"
        onClick={disposition.onDismiss}
        aria-label={t('strip.disposition.dismiss')}
        title={t('strip.disposition.dismiss')}
      >
        <span aria-hidden="true">×</span>
      </button>
    </span>
  ) : batch?.currentStep !== undefined && !stepCarried ? (
    /*
     * 🔴 **지금 스텝 문구의 대타 자리.** 원래 자리는 2행 도는 공정 칸의 배지인데, 구독이 모듈 종류를 안 실어 어느 칸이
     *    도는지 모르면 배지가 없다 — 그때만 1행이 종전(2026-09-22)대로 문구를 적는다. 아는 사실을 숨기지 않는다.
     */
    <span className="strip__notice strip__notice--step">
      <span className="strip__cur strip__cur--head" title={batch.currentStep}>
        {batch.alarm === true ? '▲ ' : ''}
        {batch.currentStep}
      </span>
    </span>
  ) : null

  /*
   * 접기/펼치기 단추 — 🔴 **1행 맨 왼쪽**이다. 두 상태에서 자리가 같고(눌러도 단추가 안 움직인다), 오른칸 E-STOP 과
   * 1행 폭만큼 떨어져 있어 오조작이 없다. 셸이 토글을 안 주면(태블릿 · 장비 관점 · 패드) 그리지 않는다.
   */
  const foldLabel = collapsed ? t('strip.foldExpand') : t('strip.foldCollapse')
  const fold =
    onToggleCollapsed === undefined ? null : (
      <button
        type="button"
        className="strip__fold"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        aria-label={foldLabel}
        title={foldLabel}
      >
        <span aria-hidden="true" className="strip__foldchev" />
      </button>
    )

  /*
   * 🔴 **이벤트 로그 단추**(디자인팀 시안 2026-09-30 · 종전 3행 오른끝에서 옮겼다). 누르면 작업 줄 아래를 이벤트 표가
   *    채운다(`StripEvents` · 셸 `useEventsFold`). 작업이 없어도 선다 — 받은 이벤트는 작업과 무관하게 쌓인다(장비 관점 · 태블릿).
   * 🔴 **`eventsOnlyStarted`(통합 콘솔)면 «시작된» 작업이 걸려 있을 때만 선다**(eric 2026-09-28 「현재 작업이
   *    진행중이지 않을 때... 이벤트.. 드랍 다운 텍스트 및 화살표 빼기..」). 작업 없음 · 시작 전(`started_at ===
   *    null`)이면 그리지 않는다.
   * ⚠ 「권한 없는 버튼은 숨기지 않고 비활성 + 사유」 규칙의 대상이 **아니다** — 그 규칙은 «할 수 있는 일을
   *   못 하는 사람»에게 이유를 보이는 것이고, 여기는 **볼 내용 자체가 없다.**
   * 🔴 **자리는 둘이다.** 기본은 1행 제어락 왼쪽(오른쪽 무리의 «맨 왼쪽»이라 빠져도 제어 · E-STOP 이 안 움직인다).
   *    `eventsInTally`(통합 콘솔) + `full` 이면 2행 요약 카드(전체 스텝 · 경과) **안 오른쪽 아래**다(eric 2026-10-06).
   *    요약 카드가 없는 `compact` 에서는 1행으로 돌아온다 — 단추가 사라지지 않는다. 접힌 줄에는 어느 쪽이든 없다.
   */
  const eventsShown =
    !collapsed && onToggleEvents !== undefined && !(eventsOnlyStarted && batch?.started !== true)
  const eventsTally = eventsInTally && variant === 'full'
  const eventsButton = eventsShown ? (
    <button
      type="button"
      className={`strip__evtog${eventsTally ? ' strip__evtog--tally' : ''}`}
      onClick={onToggleEvents}
      aria-expanded={eventsOpen}
      title={eventsOpen ? t('strip.eventsCollapse') : t('strip.eventsExpand')}
    >
      <Icon name="list" size="sm" />
      {t('action.eventsLog')}
      {eventCount ? <span className="strip__evcount num">{eventCount}</span> : null}
    </button>
  ) : null

  return (
    <section
      className={`strip strip--${variant}${collapsed ? ' strip--collapsed' : ''}${
        eventsOpen ? ' strip--events' : ''
      }${haltSafetyBadge ? ' strip--safety' : ''}`}
      aria-label={t('strip.label')}
    >
      {/* ── 1행 ─────────────────────────────────────────────────────────── */}
      <div className={`strip__head${nameFirst ? ' strip__head--named' : ''}`}>
        {fold}
        {manual ? (
          <>
            <StatusBadge status="MANUAL" label={t('enum.moduleState.MANUAL')} size="lg" />
            <b className="strip__title">{t('strip.manualTitle')}</b>
          </>
        ) : batch ? (
          <>
            {/*
             * 🔴 **읽는 순서**(디자인팀 시안 2026-09-30 §2): **상태 배지 → 작업 이름 → 담당 · 시작 → 레시피.**
             *    통합 콘솔(`nameFirst`)은 작업 이름(`Batch.name` · 테스트 작업이면 「테스트 작업」)이 서고 UUID 는 이름 링크의
             *    `title` 로만 남는다. 장비 관점 · 태블릿은 이름 칸이 없어 **UUID 전체**가 그 자리에 선다(eric 2026-09-22
             *    「배치 이름 다 보이게」). 배지가 이름 앞에 오는 것은 두 모습이 같다.
             * 🔴 **대기(`created`) 작업은 이름과 레시피를 적지 않는다**(eric 2026-09-22 「대기 상태면 배치 아이디랑
             *    레시피는 안 나와야지」). 작업 줄은 배지 「대기」(+ 시작 차단 사유 툴팁)만 말한다.
             * ⚠ 지금 스텝 문구는 1행에 없다(2026-10-01) — 2행의 도는 공정 칸 배지가 말한다.
             */}
            <WaitReasonBadge
              label={
                haltSafetyBadge
                  ? t(safety.estop === true ? 'action.estop' : 'strip.safetyStopped')
                  : batchStatusLabel(batch.status)
              }
              reason={startText}
              status={batch.status}
              {...(haltSafetyBadge ? { tone: 'danger' as const } : {})}
            />
            {/*
             * 🔴 **멈춤 한 줄(「▲ 조치가 필요합니다 N건」 · 「자동으로 이어 갑니다」 …)은 작업 줄 머리에 세우지 않는다**(eric 2026-10-29
             *    「▲ 조치가 필요합니다 2건 — 이 헤더 문구 빼줘」). [멈춘 이유 · 조치] 단추(`batchHalt.open`)가 그 역할을 한다.
             *    작업 상세 화면의 같은 줄(`HaltLineNotice`)은 그대로다. 되살리지 마라.
             */}
            {/*
             * 🔴 **고른 대기 작업은 이름이 선다**(eric 2026-10-22 「드롭다운 리스트 선택을 하면 해당 작업의 이름이 화면에 표기되게 돼」).
             *    「대기면 이름 · 레시피를 안 적는다」(2026-09-22)는 «고르기 전»의 규칙이다 — 고르는 표면(`batchChoice` · 통합 콘솔)에서는
             *    시작 전 작업이 띠에 서는 길이 «사람이 고른 것» 하나뿐이라 이름을 적는다. 레시피(`meta`)는 아직 안 적는다.
             */}
            {batch.status === 'created' && batchChoice === undefined ? null : nameFirst ? (
              onOpenBatch === undefined ? (
                <span className="strip__name" title={nameTitle}>
                  {batch.name ?? batch.id}
                </span>
              ) : (
                <button
                  type="button"
                  className="strip__name lnk"
                  onClick={onOpenBatch}
                  title={nameTitle}
                >
                  {batch.name ?? batch.id}
                  <span className="a-sr">{t('strip.openBatch')}</span>
                </button>
              )
            ) : onOpenBatch === undefined ? (
              <span className="strip__id mono" title={batch.id}>
                {batch.id}
              </span>
            ) : (
              <button
                type="button"
                className="strip__id mono lnk"
                onClick={onOpenBatch}
                title={batch.id}
              >
                {batch.id}
                <span className="a-sr">{t('strip.openBatch')}</span>
              </button>
            )}
            {/*
             * 고른 작업 풀기 × — 이름 바로 옆(작업이 서면 [작업 선택]이 숨어 바꿀 길이 이것뿐이다). 클라 선택만 지운다 —
             * 서버 요청이 없어 권한 · 제어락 · 연결과 무관하게 눌린다. 누르면 같은 자리에 [작업 선택]이 돌아온다.
             */}
            {clearChoiceShown ? (
              <button
                type="button"
                className="strip__dismiss strip__unchoose"
                onClick={onClearChoice}
                aria-label={t('strip.picker.clear')}
                title={t('strip.picker.clear')}
              >
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
            {/* 담당 · 시작 · 레시피 — 한 칸(`meta`). 좁으면 레시피부터 통째로 빠진다(위 `meta` 머리말) */}
            {batch.status === 'created' || collapsed ? null : meta}
            {/* 작업을 고른 뒤에는 상태와 무관하게 선택 버튼을 숨긴다(eric 2026-10-24). 바꾸려면 이름 옆 × 로 먼저 푼다 */}
          </>
        ) : hideStartControls ? null : batchChoice !== undefined ? (
          /* 대기 — 통합 콘솔은 같은 자리에 드롭다운이 선다(목록으로 가지 않는다) */
          <StripBatchPicker {...batchChoice} />
        ) : (
          /* 대기 — 작업 선택 버튼이 실행 중의 배지 자리(맨 왼쪽)에 온다. 두 상태의 배치가 같아야 한다 */
          <Button
            size="pill"
            {...(onSelectBatch === undefined
              ? { disabled: true as const, disabledReason: t('reason.noRouteHere') }
              : { onClick: onSelectBatch })}
          >
            <Icon name="list" size="sm" />
            {t('action.selectBatch')}
          </Button>
        )}

        {notice}

        {/*
         * 접힌 줄의 공정 한 줄 — 축약형 줄(`StripBrief`)의 한 단 얇은 꼴(`slim`)이 1행 안에 선다. 2행은 접힌 동안 없다.
         * 🔴 이상 상태는 접혀도 보인다: 상태 배지(일시정지 · 실패 …)는 위에 그대로, 안전정지 · 명령 실패는 `notice` 가,
         *    멈춘 공정은 이 줄의 배지(▲ 일시정지 · 중단)가 말한다.
         */}
        {collapsed && !manual ? (
          <StripBrief batch={batch} cells={cells} zones={zones} onOpenMonitoring={undefined} slim />
        ) : null}

        {/*
         * 🔴 왼쪽(무엇이 돌고 있나) ‖ 오른쪽(제어) 을 가르는 **빈 조각**이다. 조건부로 사라지지 않는다.
         *    종전에는 `margin-left:auto` 가 「시간 표기가 있으면 거기, 없으면 락 칩」으로 옮겨 다녔다 —
         *    조각이 하나 늘 때마다 그 규칙을 다시 써야 했다. 자리를 미는 몫을 한 곳에 둔다.
         */}
        <span className="strip__spacer" aria-hidden="true" />

        {/* 이벤트 로그 단추 — 1행 자리(제어락 왼쪽). 요약 카드로 옮겨 갔으면 여기는 비운다(위 `eventsButton` 머리말) */}
        {eventsTally ? null : eventsButton}

        {/*
         * 🔴 **제어락은 접어도 선다**(eric 2026-10-03 · 모니터링 밖은 접힘이 기본이 됐다 — 누가 락을 쥐었는지는 어느 화면에서든
         *    보여야 한다). 같은 칩 · 같은 활성 규칙(stale · 요청 중 · 권한)이다.
         * 🔴 **명령 버튼(일시정지 · 중지 · 복구)도 접어도 선다**(eric 2026-10-12 「줄였을 때 일시정지 중지 복구 버튼도 추가해줘」 — 2026-10-01
         *    회의의 「접으면 빠진다」를 뒤집었다). 같은 버튼 · 같은 활성 규칙(`allowedCommands` 그대로 · stale 이면 전부 비활성)이다.
         */}
        <ControlLockChip
          data-batch-halt-lock
          lock={lock}
          canForceRelease={permissions?.includes('settings') ?? false}
          {...(lockGateReason === undefined ? {} : { disabledReason: lockGateReason })}
          {...(onLockAction ? { onAction: onLockAction } : {})}
        />
        {hasHaltPopup ? (
          <Button size="pill" variant="primary" onClick={() => batchHaltStore.open(batch.id)}>
            <span aria-hidden="true">{HALT_GLYPH.action}</span>
            {t('batchHalt.open')}
          </Button>
        ) : null}

        {/*
         * 🔴 **작업을 고르지 않았으면(batch 없음) 시작 · 중지 · 복구를 그리지 않는다**(eric 2026-10-29 「시작 전에는 시작 중지 복구
         *    안보이게 해줘」). 「권한 없는 버튼은 숨기지 않고 비활성 + 사유」의 예외다 — 막힌 게 아니라 **대상 작업이 없다.**
         *    대기(created) 작업을 고르면 지금처럼 다 선다([시작]이 실제로 쓰인다).
         * 단추 줄이 빠지면 제어락 칩이 머리 줄 오른쪽 끝(단추 줄이 끝나던 자리)에 붙는다 — 앞의 `strip__spacer` 가 민다
         *    (eric 2026-10-29 「제어락 이거를 오른쪽 정렬로」). 작업이 있으면 칩 → 단추들 순 그대로다.
         */}
        {batch == null ? null : (
          <div className="strip__acts">
            <span className="strip__act strip__act--primary">
              <Button size="pill" variant="primary" {...actionProps(primary.command)}>
                <Icon name={primary.icon} size="sm" />
                {primary.label}
              </Button>
              {/* 사전 점검 실패로 [시작]이 막혔을 때만 — 그 자리에서 풀 단추가 있는 창을 연다(eric 2026-10-23) */}
              {primary.command === 'start' && onPreflightFixes !== undefined ? (
                <Button onClick={onPreflightFixes} size="pill">
                  {t('pfFix.open')}
                </Button>
              ) : null}
            </span>
            <span className="strip__act">
              <Button size="pill" {...actionProps('stop')}>
                <Icon name="stop" size="sm" />
                {t('action.stop')}
              </Button>
            </span>
            {/*
            🔴 **주 버튼이 이미 「복구」면 이 자리를 비운다** — 같은 말이 둘이면 사용자가
               고를 수 없다(`error` 일 때 종전부터 둘이었다. 2026-09-21 에 `paused` +
               복구 대기 까닭이 같은 얼굴을 쓰게 되면서 드러났다).
            ⚠ **버튼이 사라져도 자리는 남는다** — `.strip__act` 를 그대로 두어 오른쪽
               조작 무리(특히 E-STOP)의 가로 위치가 움직이지 않게 한다. 안전 문제다.
          */}
            <span className="strip__act">
              {batch?.resumeRoute === 'confirm' && primary.command === 'recover' ? (
                <Button size="pill" {...actionProps('resume')}>
                  <Icon name="play" size="sm" />
                  {t('action.resume')}
                </Button>
              ) : primary.command === 'recover' ? null : (
                <Button size="pill" {...actionProps('recover')}>
                  <Icon name="recover" size="sm" />
                  {t('action.recover')}
                </Button>
              )}
            </span>
          </div>
        )}

        {/*
         * 🔴 **「연결 끊김 — 제어 불가」 칩을 되살리지 마라**(2026-09-18 eric 지시로 삭제).
         *
         * 규약은 그대로다 — stale 이면 상태변경 버튼이 **전부 비활성**이고 사유도 붙는다(§6).
         * 없앤 것은 **시각적 칩 하나**이고, 사유는 버튼의 `title`(툴팁)과
         * `aria-describedby`(스크린리더)로 계속 나간다. `reasonFor()` 를 보라.
         */}
      </div>

      {/*
       * E-STOP. 확인 대화상자 없음 · 역할/제어락/상태/stale 무관 항상 활성(정본 07 §2.7).
       * 안전 상태가 unknown 이면 점선 테두리로 "안전 미확인"을 드러낸다(§5-7).
       *
       * 🔴 **1행 «안»이 아니라 작업 줄 격자의 오른쪽 칸이다**(디자인팀 시안 2026-09-30 「가장 중요한 버튼을 가장 크게」).
       *    1·2행을 세로로 걸쳐 서고(`grid-row: 1 / 3` · 높이 = 1행 + 2행) 글자는 두 줄 — 「E-STOP」 위에, 「전체 비상 정지」
       *    아래. 행 높이는 그대로다 — 버튼이 행을 늘리지 않고 행 «옆»에 선다. 접근성 이름은 두 줄을 합친 한 줄이다.
       *    왼쪽의 가는 세로선은 1·2행의 오른쪽 테(`.strip__head` · `.strip__stages` 의 `border-right`)다.
       */}
      <button
        type="button"
        className={`estop${estopped || safety.state === 'safe_shutdown' ? ' is-on' : ''}${
          safetyUnknown ? ' is-unknown' : ''
        }`}
        onClick={onEmergencyStop}
        title={t('strip.estopTitle')}
        aria-label={t('strip.estopName')}
      >
        <b className="estop__main">{t('action.estop')}</b>
        {/* 아랫줄 — 통합 콘솔은 뺀다(`estopCaption` · eric 2026-10-06). 접근 이름 · 툴팁은 그대로다 */}
        {estopCaption ? <small className="estop__sub">{t('strip.estopSub')}</small> : null}
      </button>

      {/*
        ── 2행 ─ 공정 칸 셋 + 요약 카드(`full`) · 현재 공정 한 줄(`compact`). 작업이 없어도 칸 셋(이름만)은 선다 —
        행 높이는 `grid-template-rows` 가 쥐고 있어 내용을 비워도 아래가 안 밀린다(eric 2026-09-22 「고정 지역이라 높이는 일치시키고」).
      */}
      {collapsed ? null : variant === 'compact' ? (
        <StripBrief batch={batch} cells={cells} zones={zones} onOpenMonitoring={onOpenMonitoring} />
      ) : (
        <StripStages
          batch={batch}
          cells={cells}
          zones={zones}
          /* 「00 사전 점검」 — 컨테이너가 안 주면 서버 칸(`started_at`)만으로 「완료」 · 「–」 */
          preflight={preflight ?? (batch?.started === true ? 'done' : 'idle')}
          tallyAction={eventsTally ? eventsButton : null}
          estimate={estimate}
        />
      )}

      {/* ── 이벤트 펼침 ─ 펼치면 본문을 덮는다. 레이아웃이 본문을 숨긴다 ────── */}
      {eventsOpen ? <div className="strip__events">{events}</div> : null}
    </section>
  )
}
